import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Users,
  Building2,
  Mail,
  Phone,
  MapPin,
  MoreVertical,
  Pencil,
  Trash2,
  Loader2,
  Globe,
  UserPlus,
  X,
  Star,
  Briefcase
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';

const EMPTY_CONTACT = {
  first_name: '',
  last_name: '',
  title: '',
  email: '',
  phone: '',
  mobile: '',
  is_primary: false
};

const CustomersPage = () => {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  
  const [formData, setFormData] = useState({
    company_name: '',
    industry: '',
    website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    notes: '',
    contacts: [{ ...EMPTY_CONTACT, is_primary: true }]
  });

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (err) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [token]);

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        company_name: customer.company_name || customer.company || customer.name || '',
        industry: customer.industry || '',
        website: customer.website || '',
        address_line1: customer.address_line1 || '',
        address_line2: customer.address_line2 || '',
        city: customer.city || '',
        state: customer.state || '',
        zip_code: customer.zip_code || '',
        notes: customer.notes || '',
        contacts: customer.contacts && customer.contacts.length > 0 
          ? customer.contacts 
          : [{ ...EMPTY_CONTACT, is_primary: true }]
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        company_name: '',
        industry: '',
        website: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        notes: '',
        contacts: [{ ...EMPTY_CONTACT, is_primary: true }]
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }

    // Validate at least one contact with first name
    const validContacts = formData.contacts.filter(c => c.first_name.trim());
    if (validContacts.length === 0) {
      toast.error('At least one contact with a first name is required');
      return;
    }

    // Ensure at least one primary contact
    const hasPrimary = validContacts.some(c => c.is_primary);
    if (!hasPrimary && validContacts.length > 0) {
      validContacts[0].is_primary = true;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        contacts: validContacts
      };

      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Customer updated');
      } else {
        await axios.post(`${API}/customers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Customer created');
      }
      setDialogOpen(false);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await axios.delete(`${API}/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (err) {
      toast.error('Failed to delete customer');
    }
  };

  const addContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, { ...EMPTY_CONTACT }]
    }));
  };

  const removeContact = (index) => {
    if (formData.contacts.length <= 1) {
      toast.error('At least one contact is required');
      return;
    }
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }));
  };

  const updateContact = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      )
    }));
  };

  const setPrimaryContact = (index) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) => ({
        ...contact,
        is_primary: i === index
      }))
    }));
  };

  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    const companyName = (customer.company_name || customer.company || customer.name || '').toLowerCase();
    const contactNames = (customer.contacts || []).map(c => 
      `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase()
    ).join(' ');
    return companyName.includes(searchLower) || contactNames.includes(searchLower);
  });

  const getPrimaryContact = (customer) => {
    if (!customer.contacts || customer.contacts.length === 0) return null;
    return customer.contacts.find(c => c.is_primary) || customer.contacts[0];
  };

  const getInitials = (customer) => {
    const name = customer.company_name || customer.company || customer.name || '?';
    return name.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Customers</h1>
          <p className="text-slate-500 mt-1">Manage your clients and contacts</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => handleOpenDialog()} 
              className="bg-[#1F3C4F] hover:bg-[#152a38]"
              data-testid="add-customer-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Company Information */}
              <div className="space-y-4">
                <h3 className="font-heading font-semibold text-[#1F3C4F] flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Company Information
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Company name"
                      data-testid="customer-company-name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Input
                      id="industry"
                      value={formData.industry}
                      onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                      placeholder="e.g., Healthcare, Technology"
                      data-testid="customer-industry"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="website"
                        className="pl-9"
                        value={formData.website}
                        onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                        placeholder="www.company.com"
                        data-testid="customer-website"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4">
                <h3 className="font-heading font-semibold text-[#1F3C4F] flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </h3>
                
                <div className="space-y-3">
                  <Input
                    value={formData.address_line1}
                    onChange={(e) => setFormData(prev => ({ ...prev, address_line1: e.target.value }))}
                    placeholder="Street address"
                    data-testid="customer-address1"
                  />
                  <Input
                    value={formData.address_line2}
                    onChange={(e) => setFormData(prev => ({ ...prev, address_line2: e.target.value }))}
                    placeholder="Suite, unit, building (optional)"
                    data-testid="customer-address2"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="City"
                      data-testid="customer-city"
                    />
                    <Input
                      value={formData.state}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      placeholder="State"
                      data-testid="customer-state"
                    />
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                      placeholder="ZIP"
                      data-testid="customer-zip"
                    />
                  </div>
                </div>
              </div>

              {/* Contacts */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-[#1F3C4F] flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Contacts
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addContact}
                    data-testid="add-contact-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Add Contact
                  </Button>
                </div>

                {formData.contacts.map((contact, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-3 relative">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">Contact {index + 1}</span>
                        {contact.is_primary && (
                          <Badge className="bg-[#1F3C4F] text-white text-xs">Primary</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!contact.is_primary && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setPrimaryContact(index)}
                            className="text-xs"
                          >
                            <Star className="w-3 h-3 mr-1" />
                            Set Primary
                          </Button>
                        )}
                        {formData.contacts.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeContact(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">First Name *</Label>
                        <Input
                          value={contact.first_name}
                          onChange={(e) => updateContact(index, 'first_name', e.target.value)}
                          placeholder="First name"
                          data-testid={`contact-${index}-firstname`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Last Name</Label>
                        <Input
                          value={contact.last_name}
                          onChange={(e) => updateContact(index, 'last_name', e.target.value)}
                          placeholder="Last name"
                          data-testid={`contact-${index}-lastname`}
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Title / Role</Label>
                      <Input
                        value={contact.title}
                        onChange={(e) => updateContact(index, 'title', e.target.value)}
                        placeholder="e.g., Marketing Director, CEO"
                        data-testid={`contact-${index}-title`}
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="email"
                          className="pl-9"
                          value={contact.email}
                          onChange={(e) => updateContact(index, 'email', e.target.value)}
                          placeholder="email@company.com"
                          data-testid={`contact-${index}-email`}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Office Phone</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            className="pl-9"
                            value={contact.phone}
                            onChange={(e) => updateContact(index, 'phone', e.target.value)}
                            placeholder="(555) 555-5555"
                            data-testid={`contact-${index}-phone`}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Mobile Phone</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            className="pl-9"
                            value={contact.mobile}
                            onChange={(e) => updateContact(index, 'mobile', e.target.value)}
                            placeholder="(555) 555-5555"
                            data-testid={`contact-${index}-mobile`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes about this customer..."
                  rows={3}
                  data-testid="customer-notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleSubmit} 
                disabled={saving}
                className="bg-[#1F3C4F] hover:bg-[#152a38]"
                data-testid="save-customer-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingCustomer ? 'Update Customer' : 'Create Customer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by company or contact name..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="customer-search"
        />
      </div>

      {/* Customer Cards */}
      {filteredCustomers.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500 text-center">
              {searchTerm ? 'No customers match your search' : 'No customers yet. Add your first customer!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCustomers.map((customer) => {
            const primaryContact = getPrimaryContact(customer);
            const displayName = customer.company_name || customer.company || customer.name || 'Unknown';
            
            return (
              <Card 
                key={customer.id} 
                className="border-0 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`customer-card-${customer.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-full bg-[#1F3C4F]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-[#1F3C4F] font-heading font-bold text-lg">
                          {getInitials(customer)}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-heading font-semibold text-lg truncate">{displayName}</h3>
                        {customer.industry && (
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            {customer.industry}
                          </p>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenDialog(customer)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(customer.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Primary Contact */}
                  {primaryContact && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Primary Contact</p>
                      <p className="font-medium text-sm">
                        {primaryContact.first_name} {primaryContact.last_name}
                        {primaryContact.title && (
                          <span className="text-slate-500 font-normal"> • {primaryContact.title}</span>
                        )}
                      </p>
                      {primaryContact.email && (
                        <a href={`mailto:${primaryContact.email}`} className="text-sm text-[#1F3C4F] hover:underline flex items-center gap-1 mt-1">
                          <Mail className="w-3 h-3" />
                          {primaryContact.email}
                        </a>
                      )}
                      {primaryContact.phone && (
                        <a href={`tel:${primaryContact.phone}`} className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3" />
                          {primaryContact.phone}
                        </a>
                      )}
                    </div>
                  )}

                  {/* Additional Contacts Count */}
                  {customer.contacts && customer.contacts.length > 1 && (
                    <button
                      onClick={() => setExpandedCustomer(expandedCustomer === customer.id ? null : customer.id)}
                      className="mt-3 text-sm text-[#1F3C4F] hover:underline"
                    >
                      {expandedCustomer === customer.id ? 'Hide' : 'Show'} {customer.contacts.length - 1} more contact{customer.contacts.length > 2 ? 's' : ''}
                    </button>
                  )}

                  {/* Expanded Contacts */}
                  {expandedCustomer === customer.id && customer.contacts && (
                    <div className="mt-3 space-y-2">
                      {customer.contacts.filter(c => !c.is_primary).map((contact, idx) => (
                        <div key={idx} className="p-2 bg-slate-50 rounded text-sm">
                          <p className="font-medium">
                            {contact.first_name} {contact.last_name}
                            {contact.title && <span className="text-slate-500 font-normal"> • {contact.title}</span>}
                          </p>
                          {contact.email && (
                            <a href={`mailto:${contact.email}`} className="text-[#1F3C4F] hover:underline text-xs">{contact.email}</a>
                          )}
                          {contact.phone && (
                            <p className="text-xs text-slate-500">{contact.phone}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Address */}
                  {(customer.address_line1 || customer.city) && (
                    <div className="mt-4 pt-3 border-t border-slate-100 text-sm text-slate-500 flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        {customer.address_line1 && <p>{customer.address_line1}</p>}
                        {(customer.city || customer.state || customer.zip_code) && (
                          <p>{[customer.city, customer.state, customer.zip_code].filter(Boolean).join(', ')}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Website */}
                  {customer.website && (
                    <div className="mt-2 text-sm">
                      <a 
                        href={customer.website.startsWith('http') ? customer.website : `https://${customer.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1F3C4F] hover:underline flex items-center gap-1"
                      >
                        <Globe className="w-3 h-3" />
                        {customer.website}
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
